export const templates = {
  hogwartsMarkdown: `# 🔍 [Case Title / Document]

## 📋 Case Overview
**Case Number:**
**Date Opened:**
**Location:**
**Client:**
**Priority Level:**

> "The truth is written in the residue of every spell—you just need to know where to look."
> — *Anonymous Investigator*

---

## 🗂️ Case Summary

### 📝 Initial Report
Brief description of the case, incident, or investigation.
What was the inciting event? Who requested the investigation?

### 👥 Key Individuals
| Name | Role | Affiliation | Suspicion Level | Notes |
|------|------|-------------|-----------------|-------|
|      |      |             | 🟢 / 🟡 / 🔴    |       |
|      |      |             | 🟢 / 🟡 / 🔴    |       |

---

## 🔎 Evidence & Clues

### 🧪 Physical Evidence
- **Item:**
- **Location Found:**
- **Magical Signature:**
- **Analysis:**

### 🪄 Magical Residue
- **Type of Magic:**
- **Estimated Time:**
- **Caster Profile:**
- **Spell Components:**

### 📜 Documents & Records
1. **Source:**
2. **Contents:**
3. **Relevance:**
4. **Cross-Reference:**

---

## 🎯 Suspects & Witnesses

### 🔴 Primary Suspects
| Name | Motive | Opportunity | Alibi Status | Evidence |
|------|--------|-------------|--------------|----------|
|      |        |             |              |          |

### 🟡 Persons of Interest
- **Name:**
- **Connection:**
- **Statement:**
- **Follow-up Required:**

### 🟢 Witnesses
- **Name:**
- **What They Saw:**
- **Reliability:**
- **Corroboration:**

---

## 🗺️ Field Investigation

### 📍 Scene Details
**Primary Location:**
**Condition:**
**Wards/Protections:**
**Access Points:**

### 🔦 Scene Analysis
- [ ] Photographs taken
- [ ] Magical traces collected
- [ ] Witness statements recorded
- [ ] Evidence catalogued
- [ ] Perimeter checked

### ⚔️ Incidents / Confrontations
- **Encounter:**
- **Response:**
- **Outcome:**
- **Casualties/Damage:**

---

## 🧩 Theory & Analysis

### 💭 Working Theory
Current hypothesis about what happened:


### 🔗 Connections
Links between evidence, suspects, and timeline:
-
-
-

### ❓ Unanswered Questions
-
-
-

### ⚠️ Inconsistencies
What doesn't add up:
-
-

---

## ⏱️ Timeline

| Time | Event | Source | Verified |
|------|-------|--------|----------|
|      |       |        | ✓ / ✗    |
|      |       |        | ✓ / ✗    |

---

## 📞 Contacts & Resources

### 🏛️ Official Channels
- **Ministry Contact:**
- **Auror Liaison:**
- **Records Access:**

### 🤝 Informants
- **Name:**
- **Specialty:**
- **Cost:**
- **Reliability:**

---

## 💼 Equipment & Tools

### 🔧 Field Kit
- Wand:
- Detection Charms:
- Evidence Containers:
- Communication Device:
- Emergency Supplies:

### 📖 Reference Materials
- Case Files Referenced:
- Spell Research:
- Legal Statutes:

---

## 📊 Progress Report

### ✅ Completed
- [ ]
- [ ]

### 🔄 In Progress
- [ ]
- [ ]

### ⏳ Pending
- [ ]
- [ ]

---

## 🎯 Next Actions

**Immediate (24h):**
- [ ]
- [ ]

**Short Term (Week):**
- [ ]
- [ ]

**Long Term:**
- [ ]
- [ ]

---

## 💰 Case Expenses

| Item | Cost | Status | Notes |
|------|------|--------|-------|
|      |      | Paid / Pending |  |

---

## 🧠 Personal Notes

### 🤔 Gut Feelings
Instinct says...


### 📝 Private Observations
Things that don't go in the official report:


### ⚖️ Ethical Considerations
Moral gray areas in this case:


---

## 🔗 Related Cases
- **Case #:**
- **Connection:**
- **Status:**

---

## 💭 Case Quotes
> "Everyone has something to hide. The question is whether it's relevant."
> — Field Notes

---

## 📎 Quick Reference

**Common Investigation Spells**

| Spell | Purpose | Notes |
|--------|---------|-------|
| Prior Incantato | Reveals last spell cast | Requires wand contact |
| Revelio | Detects hidden objects/people | Limited by wards |
| Specialis Revelio | Identifies magical properties | Good for evidence |

---

## 🎨 Evidence Tags
Use for marking evidence priority:
🔴 **Critical** • 🟡 **Important** • 🟢 **Minor** • ⚪ **Irrelevant**

---
`,
  spell: `## 🔮 [Spell Name]

**📊 Stats:**
- **Level:**
- **Casting Time:**
- **Range:**
- **Components:**
- **Duration:**

**📝 Description:**


**⚔️ Combat Use:**


**🎯 Strategic Notes:**


---

`,
  session: `# 📅 Session ${new Date().toLocaleDateString()}

## 📖 Session Summary


## 🔍 Case Progress
**Active Cases:**
-
-

**Breakthroughs:**
-

**Dead Ends:**
-

## 🗣️ Important Contacts
| Name | Role | Information Provided | Trustworthiness |
|------|------|----------------------|-----------------|
|      |      |                      | 🟢 / 🟡 / 🔴   |

## 🔎 Clues Discovered
| Clue | Location | Significance | Follow-up |
|------|----------|--------------|-----------|
|      |          |              |           |

## 🗺️ Locations Investigated
-

## 🎯 Suspects & Leads
**New Suspects:**
-

**Alibis Verified/Broken:**
-

**Persons of Interest:**
-

## ⚔️ Confrontations & Action
**Magical Combat:**
-

**Interrogations:**
-

**Chases/Escapes:**
-

## 💰 Expenses & Compensation
**Case Fees Earned:**
-

**Expenses:**
-

**Net:**
-

## 📝 Character Development
**Relationships Changed:**
-

**Skills Improved:**
-

**Moral Dilemmas:**
-

## 🎯 Next Session Goals
- [ ] Follow up on lead:
- [ ] Interview:
- [ ] Investigate location:
- [ ] Research:

## 💭 Player Notes & Theories


---

`,
  relationship: `## 👥 Relationship: [NPC Name]

**📊 Relationship Status:** 
**🎭 Their Personality:** 
**🎯 Their Goals:** 
**💭 What They Think of Me:** 
**🤝 How I Can Help Them:** 
**⚠️ Potential Conflicts:** 

### 📝 Interaction History


### 🎯 Future Plans


---

`,
  creature: `## 🐉 [Creature Name]

**📊 Basic Stats:**
- **AC:** 
- **HP:** 
- **Speed:** 
- **CR:** 
- **Size:** 
- **Type:** 

**💪 Ability Scores:**
| STR | DEX | CON | INT | WIS | CHA |
|-----|-----|-----|-----|-----|-----|
|     |     |     |     |     |     |

**🎯 Skills & Senses:**
- **Skills:** 
- **Senses:** 
- **Damage Resistances:** 
- **Damage Immunities:** 
- **Condition Immunities:** 

**✨ Special Abilities:**


**⚔️ Actions:**


**🌟 Legendary Actions:** *(if applicable)*


**📖 Lore & Description:**


**🎯 Tactical Use:**
- **Combat Role:** 
- **Environment:** 
- **Allies:** 

---

`,
};
